import React, { useEffect } from 'react';
import { X, Plus, Bell, ExternalLink } from 'lucide-react';
import { ScanResultDto } from '../types/api';
import {
  parseIndicators, gateOf, rsiTone, GateStatus, emaSpreadPct, fmtINR, THRESHOLDS,
} from '../utils/signal';
import { RsiGauge, Sparkline, SignalPill, signalColor } from './Indicators';

const KNOB: Record<GateStatus, string> = { pass: '✓', warn: '~', fail: '✕' };

/** Slide-in drawer with the full RSI → ADX → EMA breakdown for one symbol. */
export function StockDetail({ result, onClose }: { result: ScanResultDto; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const cls = result.classification ?? 'NONE';
  const { rsi, adx } = parseIndicators(result);
  const gate = gateOf(result);
  const spread = emaSpreadPct(result);
  const color = signalColor(cls);

  const steps = [
    {
      n: 1, name: 'RSI momentum', st: gate[0],
      val: rsi != null ? rsi.toFixed(1) : '—',
      desc: rsi == null ? 'No RSI data'
        : rsi >= THRESHOLDS.rsiOverbought ? 'Strong — overbought territory'
        : rsi <= THRESHOLDS.rsiOversold ? 'Weak — oversold'
        : rsi >= THRESHOLDS.rsi.pass ? 'Healthy upward momentum'
        : 'Momentum fading',
    },
    {
      n: 2, name: 'ADX trend strength', st: gate[1],
      val: adx != null ? adx.toFixed(1) : '—',
      desc: adx == null ? 'No ADX data'
        : adx >= THRESHOLDS.adx.pass ? 'Trending — strong directional move'
        : adx >= THRESHOLDS.adx.warn ? 'Borderline — trend still forming'
        : 'Choppy — no clear trend',
    },
    {
      n: 3, name: 'EMA7 vs EMA26', st: gate[2],
      val: spread != null ? `${spread >= 0 ? '+' : ''}${spread.toFixed(1)}%` : '—',
      desc: result.emaFast != null && result.emaSlow != null
        ? (result.emaFast > result.emaSlow ? 'EMA7 above EMA26 — uptrend' : 'EMA7 below EMA26 — downtrend')
        : 'No EMA data',
    },
  ];

  return (
    <div className="drawer-scrim" onClick={onClose}>
      <aside className={`drawer signal-${cls.toLowerCase()}`} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="dr-head">
          <div>
            <div className="dr-sym">
              {result.symbol}
              <SignalPill classification={cls} score={result.score} />
            </div>
            <div className="dr-meta">Last scan {new Date(result.createdAt).toLocaleString('en-IN')}</div>
          </div>
          <button className="dr-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>

        <div className="dr-price">
          <span className="dr-px">{fmtINR(result.currentPrice)}</span>
        </div>

        {result.priceHistory && result.priceHistory.length > 1 && (
          <div className="dr-chart"><Sparkline values={result.priceHistory} color={color} height={150} /></div>
        )}

        <div className="dr-rsi">
          <div className="dr-rsi-top">
            <span className="rsi-label">RSI · first read</span>
            <span className={`rsi-num ${rsiTone(rsi)}`} style={{ fontSize: 34 }}>{rsi != null ? rsi.toFixed(1) : '—'}</span>
          </div>
          <RsiGauge value={rsi} />
          <div className="dr-rsi-scale"><span>0</span><span>30 oversold</span><span>70 overbought</span><span>100</span></div>
        </div>

        <div className="dr-section-label">Signal gate · RSI → ADX → EMA</div>
        <div className="dr-gate">
          {steps.map(s => (
            <div className={`dr-step ${s.st}`} key={s.n}>
              <span className="dr-step-knob">{KNOB[s.st]}</span>
              <div className="dr-step-body">
                <div className="dr-step-top"><b>{s.n}. {s.name}</b><span className="dr-step-val">{s.val}</span></div>
                <div className="dr-step-desc">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="dr-metrics">
          {[
            ['EMA7', fmtINR(result.emaFast)],
            ['EMA26', fmtINR(result.emaSlow)],
            ['ADX', adx != null ? adx.toFixed(1) : '—'],
            ['Score', String(result.score)],
          ].map(([k, v]) => (
            <div className="dr-metric" key={k}><span className="dr-m-lbl">{k}</span><span className="dr-m-val">{v}</span></div>
          ))}
        </div>

        {result.signalReason && <div className="dr-reason">{result.signalReason}</div>}

        <div className="dr-actions">
          <button className="btn btn-primary"><Plus size={16} style={{ marginRight: 6 }} />Watchlist</button>
          <button className="btn btn-secondary"><Bell size={16} style={{ marginRight: 6 }} />Alert</button>
          <button className="btn btn-secondary"><ExternalLink size={16} style={{ marginRight: 6 }} />Chart</button>
        </div>
      </aside>
    </div>
  );
}
